import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  Grid,
  Paper,
  Box,
  Button,
  //   InputAdornment,
} from "@mui/material";

// Simple mapping of goals -> recommended protein ranges (g per kg)
const GOAL_RANGES = {
  maintain: { label: "Maintenance", min: 1.2, max: 1.6, default: 1.4 },
  build: { label: "Build muscle", min: 1.6, max: 2.2, default: 2.0 },
  cut: {
    label: "Lose fat (preserve muscle)",
    min: 1.8,
    max: 2.5,
    default: 2.2,
  },
  gain: { label: "Gain weight (aggressive)", min: 1.8, max: 2.4, default: 2.1 },
};

export default function ProteinCalculatorApp() {
  const [weight, setWeight] = useState(0);
  const [Age, setAge] = useState(0);
  const [goal, setGoal] = useState("build");
  const [meals, setMeals] = useState(0);
  const [multiplier, setMultiplier] = useState(GOAL_RANGES["build"].default);

  // when the goal changes, move multiplier into that goal's default range
  const onGoalChange = (g) => {
    setGoal(g);
    setMultiplier(GOAL_RANGES[g].default);
  };

  const range = GOAL_RANGES[goal];

  const proteinPerDay = useMemo(() => {
    const w = parseFloat(weight) || 0;
    return Math.round(w * multiplier);
  }, [weight, multiplier]);

  const proteinPerMeal = useMemo(() => {
    return meals > 0 ? Math.round(proteinPerDay / meals) : 0;
  }, [proteinPerDay, meals]);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          alignItems: "center",
          textAlign: "center",
          bgcolor: "#080808ff",
          color: "white",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Protein Intake Calculator
        </Typography>
        <Typography variant="body2" color="white" gutterBottom>
          Interactive calculator using your weight, goal, and preferred
          multiplier to calculate daily protein target and per-meal allocation.
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{ mt: 4 }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12} sm={6}>
            <TextField
              label="Weight (kg)"
              type="number"
              fullWidth
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              InputProps={
                {
                  // endAdornment: (
                  //   <InputAdornment position="end" sx={{ color: "white" }}>
                  //     kg
                  //   </InputAdornment>
                  // ),
                }
              }
              inputProps={{ style: { color: "white" } }}
              InputLabelProps={{ style: { color: "white" } }}
              sx={{
                height: 56,
                width: 200,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#2d8a82ff" },
                  "&:hover fieldset": { borderColor: "white" },
                  "&.Mui-focused fieldset": { borderColor: "white" },
                  color: "white",
                },
                "& .MuiInputLabel-root": { color: "white" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Age"
              type="number"
              fullWidth
              value={Age}
              onChange={(e) => setAge(e.target.value)}
              InputProps={{
                // endAdornment: (
                //   <InputAdornment position="end" sx={{ color: "white" }}>
                //     years
                //   </InputAdornment>
                // ),
                style: { color: "white" },
              }}
              InputLabelProps={{ style: { color: "white" } }}
              sx={{
                height: 56,
                width: 200,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#2d8a82ff" },
                  "&:hover fieldset": { borderColor: "white" },
                  "&.Mui-focused fieldset": { borderColor: "white" },
                  color: "white",
                },
                "& .MuiInputLabel-root": { color: "white" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="goal-label" sx={{ color: "white" }}>
                Goal
              </InputLabel>
              <Select
                labelId="goal-label"
                value={goal}
                label="Goal"
                onChange={(e) => onGoalChange(e.target.value)}
                sx={{
                  height: 56,
                  width: 200,
                  color: "white",
                  bgcolor: "#080808ff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2d8a82ff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "& .MuiSelect-icon": { color: "white" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#2d8a82ff",
                      color: "white",
                    },
                  },
                }}
              >
                {Object.entries(GOAL_RANGES).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Meals per day"
              type="number"
              fullWidth
              value={meals}
              onChange={(e) =>
                setMeals(Math.max(1, parseInt(e.target.value || 1)))
              }
              InputProps={{
                style: { color: "white" },
              }}
              InputLabelProps={{ style: { color: "white" } }}
              sx={{
                height: 56,
                width: 200,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#2d8a82ff" },
                  "&:hover fieldset": { borderColor: "white" },
                  "&.Mui-focused fieldset": { borderColor: "white" },
                  color: "white",
                },
                "& .MuiInputLabel-root": { color: "white" },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom sx={{ textAlign: "center" }}>
              Recommended range: {range.min} - {range.max} g/kg — Current:{" "}
              {multiplier} g/kg
            </Typography>
            <Slider
              value={multiplier}
              min={Math.max(0.8, range.min - 0.4)}
              max={range.max + 0.4}
              step={0.05}
              onChange={(e, v) =>
                setMultiplier(typeof v === "number" ? v : v[0])
              }
              valueLabelDisplay="auto"
              marks={[
                { value: range.min, label: `min ${range.min}` },
                { value: range.max, label: `max ${range.max}` },
              ]}
              sx={{
                "& .MuiSlider-markLabel": { color: "white" },
                "& .MuiSlider-thumb": {
                  color: "#2d8a82ff",
                  border: "2px solid #2d8a82ff",
                },
                "& .MuiSlider-track": {
                  color: "#2d8a82ff",
                  bgcolor: "#2d8a82ff",
                }, // active bar
                "& .MuiSlider-rail": {
                  color: "#2d8a82ff",
                  bgcolor: "#2d8a82ff",
                }, // inactive bar
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: "center",
                color: "white",
                bgcolor: "#2d8a82ff",
              }}
            >
              <Typography variant="h6">Results</Typography>
              <Typography sx={{ mt: 1 }}>
                Daily protein target: <strong>{proteinPerDay} g</strong>
              </Typography>
              <Typography>
                Per meal ({meals} meals): <strong>{proteinPerMeal} g</strong>
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">
                  Quick Food Equivalents (approx.)
                </Typography>
                <Typography variant="body2">
                  • 200 g chicken breast = 60 g protein
                </Typography>
                <Typography variant="body2">
                  • 1 scoop whey = 24 g protein
                </Typography>
                <Typography variant="body2">
                  • 200 g Greek yogurt = 20 g protein
                </Typography>
                <Typography variant="body2">
                  • 3 large eggs = 18 g protein
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setWeight(0);
                  setAge(0);
                  setGoal("build");
                  setMeals(0);
                  setMultiplier(GOAL_RANGES["build"].default);
                }}
                sx={{
                  bgcolor: "#2d8a82ff",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#e0e0e0", // light gray on hover
                    color: "black",
                  },
                }}
              >
                Reset to goal default
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="white"
              sx={{ mt: 2, textAlign: "center" }}
            >
              Tip: If you're cutting (calorie deficit) aim for the higher end of
              the recommended range to preserve muscle. If you're bulking
              slowly, staying near the middle works well.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
